import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Megaphone, QrCode, Share2, MousePointerClick, Scale } from "lucide-react";

export default function BringToCampaign() {
  const [munis, setMunis] = useState([]);
  const [elections, setElections] = useState([]);
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState({
    legal_name: "", public_name: "", email: "", phone: "",
    municipality_id: "", election_id: "", office_id: "",
    campaign_website: "", facebook: "", instagram: "", x: "", youtube: "",
    official_candidate_list_url: "", why_join: "",
    paper_ballot_position: "not_stated", paper_ballot_statement: "",
    accessibility_contact: "", how_heard: "", agrees_to_rules: false,
  });
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data));
  }, []);
  useEffect(() => {
    if (!form.municipality_id) return;
    api.get("/public/elections", { params: { municipality_id: form.municipality_id } }).then((r) => setElections(r.data));
  }, [form.municipality_id]);
  useEffect(() => {
    if (!form.election_id) return;
    api.get("/public/offices", { params: { election_id: form.election_id } }).then((r) => setOffices(r.data));
  }, [form.election_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid =
    form.legal_name.length >= 2 && form.public_name.length >= 2 && /.+@.+\..+/.test(form.email) &&
    form.municipality_id && form.election_id && form.office_id && form.agrees_to_rules;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, website: hp };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.website = hp || "";
      const r = await api.post("/public/applications", payload);
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please review your entries.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="wyv-container py-16">
        <Card className="max-w-xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-700" />
            </div>
            <h1 className="font-heading font-bold text-xl text-foreground" data-testid="application-success-title">Application received</h1>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{done.message}</p>
            <p className="text-xs text-muted-foreground mt-4">
              Approval is based only on equal-access criteria: you are a legitimate registered candidate and agree to the
              platform rules — never on political beliefs, party, or paper-ballot position.
            </p>
          </CardContent>
        </Card>
      </div>
    );

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="bring-campaign-title">
          Bring Witness Your Vote to your campaign
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Every legitimate registered candidate in an Ontario municipal election can participate — on equal terms,
          free of charge. We verify each applicant against the official candidate list.
        </p>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mt-8 max-w-3xl">
        {[
          { icon: Megaphone, label: "Your own declaration link" },
          { icon: QrCode, label: "QR code for print materials" },
          { icon: Share2, label: "Ready-made share messages" },
          { icon: MousePointerClick, label: "Website button snippet" },
        ].map((f) => (
          <div key={f.label} className="rounded-lg border border-slate-200 bg-white p-4 text-center">
            <f.icon className="h-5 w-5 text-accent mx-auto mb-2" />
            <p className="text-xs font-medium text-foreground">{f.label}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="mt-8 max-w-2xl">
        <Card>
          <CardContent className="p-6 sm:p-8 space-y-5">
            <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Legal name *</Label>
                <Input className="mt-1.5" value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="application-legal-name" />
              </div>
              <div>
                <Label>Public / campaign name *</Label>
                <Input className="mt-1.5" value={form.public_name} onChange={(e) => set("public_name", e.target.value)} data-testid="application-public-name" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="application-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input className="mt-1.5" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="application-phone" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>Municipality *</Label>
                <Select value={form.municipality_id} onValueChange={(v) => set("municipality_id", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="application-municipality"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Election *</Label>
                <Select value={form.election_id} onValueChange={(v) => set("election_id", v)} disabled={!form.municipality_id}>
                  <SelectTrigger className="mt-1.5" data-testid="application-election"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {elections.map((e) => <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Office / Ward *</Label>
                <Select value={form.office_id} onValueChange={(v) => set("office_id", v)} disabled={!form.election_id}>
                  <SelectTrigger className="mt-1.5" data-testid="application-office"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Campaign website</Label>
              <Input className="mt-1.5" value={form.campaign_website} onChange={(e) => set("campaign_website", e.target.value)} placeholder="https://…" data-testid="application-website" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Facebook</Label>
                <Input className="mt-1.5" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
              </div>
              <div>
                <Label>X (Twitter)</Label>
                <Input className="mt-1.5" value={form.x} onChange={(e) => set("x", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Link to the official candidate list showing your registration</Label>
              <Input className="mt-1.5" value={form.official_candidate_list_url} onChange={(e) => set("official_candidate_list_url", e.target.value)} placeholder="https://… (helps us verify faster)" data-testid="application-official-list" />
            </div>
            <div>
              <Label>Why do you want to participate?</Label>
              <Textarea className="mt-1.5" rows={4} value={form.why_join} onChange={(e) => set("why_join", e.target.value)} data-testid="application-why-join" />
            </div>
            <div>
              <Label className="mb-2 block">Paper-ballot position (optional — never affects approval)</Label>
              <RadioGroup value={form.paper_ballot_position} onValueChange={(v) => set("paper_ballot_position", v)} className="space-y-1.5">
                <div className="flex items-center gap-2"><RadioGroupItem value="supports" id="pb-s" /><Label htmlFor="pb-s" className="font-normal">I support paper ballots</Label></div>
                <div className="flex items-center gap-2"><RadioGroupItem value="custom" id="pb-c" /><Label htmlFor="pb-c" className="font-normal">Custom position (statement below)</Label></div>
                <div className="flex items-center gap-2"><RadioGroupItem value="not_stated" id="pb-n" /><Label htmlFor="pb-n" className="font-normal">Prefer not to state</Label></div>
              </RadioGroup>
            </div>
            {form.paper_ballot_position !== "not_stated" && (
              <div>
                <Label>Paper-ballot statement (max 1000 characters)</Label>
                <Textarea className="mt-1.5" rows={3} maxLength={1000} value={form.paper_ballot_statement} onChange={(e) => set("paper_ballot_statement", e.target.value)} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Accessibility contact (optional)</Label>
                <Input className="mt-1.5" value={form.accessibility_contact} onChange={(e) => set("accessibility_contact", e.target.value)} />
              </div>
              <div>
                <Label>How did you hear about us?</Label>
                <Input className="mt-1.5" value={form.how_heard} onChange={(e) => set("how_heard", e.target.value)} />
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-start gap-3">
                <Scale className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Platform rules (equal for every candidate):</strong> no access to live
                  totals while voting is open; no access to declarant identities or voter data — ever; never ask a voter
                  for a PIN, password, Voter Information Letter or ballot image; no claims that this platform is an
                  official voting system.
                </p>
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.agrees_to_rules} onCheckedChange={(v) => set("agrees_to_rules", !!v)} className="mt-0.5" data-testid="application-agree-rules" />
              <span className="text-sm text-foreground leading-relaxed">
                I am (or will be) a registered candidate in this election and I agree to the platform rules. *
              </span>
            </label>

            <Button type="submit" className="w-full h-11" disabled={!valid || submitting} data-testid="application-submit-button">
              {submitting ? "Submitting…" : "Submit application"}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
